/*******************************************************************************
  AdditionalRoundSettings.js

  Author: Advait
*******************************************************************************/

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API } from "aws-amplify";
import { selectSite, updateSite as updateSiteInStore } from "../store/siteSlice";
import { updateRoundSettings } from "../apihelpers/graphqlhelpers";
import { showNotification } from "./helper/notification";
import "../index.css";
import LocationSelection from "./LocationSelection";

//***************************************************************************
// This component handles include tag in round values.
// Initial values to display come from the redux store.
//***************************************************************************
function AdditionalRoundSettings({ scheduleType }) {
  const dispatch = useDispatch();
  const selectedSite = useSelector(selectSite);
  const [tagsState, setTagsState] = useState(
    selectedSite ? selectedSite.include_tags_in_round : {}
  );
  const [allowPatrolTpApp, setAllowPatrolTpApp] = useState(
    selectedSite ? selectedSite.allow_securityRound_tpapp : false
  );
  const [wifiThreshold, setWifiThreshold] = useState(
    selectedSite?.alarmThresholds?.wifi?.lowWarning
  );
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [inspectionTagsData, setInspectionTagsData] = useState([]);
  const [doorDetectLocations, setDoorDetectLocations] = useState(
    selectedSite?.door_detect_locations || []
  );
  const [displayDetectLocations, setDisplayDetectLocations] = useState(
    selectedSite?.display_detect_locations || []
  );

  useEffect(() => {
    async function fetchInspectionTags() {
      if (scheduleType === "inspection" && selectedSite && selectedSite.id) {
        try {
          const response = await API.get(
            "fataskman",
            `/sites/${selectedSite.id}/inspectiontags`
          );
          setInspectionTagsData(response.inspectionTags || []);
        } catch (err) {
          console.error("Error fetching inspection tags:", err);
        }
      }
    }
    fetchInspectionTags();
  }, [selectedSite, scheduleType]);

  const handleLocationChange = (detectionType, updatedLocations) => {
    if (detectionType === "door") {
      setDoorDetectLocations(updatedLocations);
    } else if (detectionType === "display") {
      setDisplayDetectLocations(updatedLocations);
    }
    setSaveButtonDisabled(false);
  };

  const handleCheckboxChange = (event) => {
    event.persist();
    setSaveButtonDisabled(false);
    const { name, checked } = event.target;
    //console.log(`name : ${name}, checked: ${checked}`);
    const newState = { ...tagsState };
    // Update state directly based on the checkbox name
    if (name === "inspectionTag") {
      newState.inspection_patrol = checked;
    } else if (name === "meetingRoomTag") {
      // Update the appropriate property based on scheduleType
      if (scheduleType === "inspection") {
        newState.meetingroom_inspection = checked;
      } else {
        newState.meetingroom_patrol = checked;
      }
    } else if (name === "allowPatrolTpApp") {
      setAllowPatrolTpApp(checked);
    }
    setTagsState(newState);
  };

  //***************************************************************************
  // Update the values to the database and update the store.
  //***************************************************************************
  async function handleSaveBehavior(e) {
    setSaveButtonDisabled(true);
    try {
      const thresholds = { ...selectedSite.alarmThresholds };
      thresholds.wifi = { lowWarning: parseInt(wifiThreshold) };
      const response = await updateRoundSettings(
        selectedSite.id,
        tagsState,
        allowPatrolTpApp,
        thresholds,
        doorDetectLocations,
        displayDetectLocations
      );
      showNotification(response);
      dispatch(updateSiteInStore(response.data.updateSite));
    } catch (e) {
      showNotification();
    }
  }

  //***************************************************************************
  // render the UI
  //***************************************************************************
  if (!selectedSite) return null;

  const isInspection = scheduleType === "inspection";
  return (
    <div className="individualContainer">
      <table className="individualContainerTable">
        <tbody>
          <tr>
            <td className="individualContainerTitleColumn" valign="top">
              <h4 className="individualContainerTitleHeading">Additional Settings</h4>
            </td>
            <td>
              <table>
                <tbody>
                  <tr>
                    <td className="subTitleColumnAdditionalSetting">
                      <label htmlFor="meetingRoomTag">Include Telepresence Destinations</label>
                    </td>
                    <td valign="top">
                      <input
                        id="meetingRoomTag"
                        name="meetingRoomTag"
                        type="checkbox"
                        checked={
                          isInspection
                            ? tagsState.meetingroom_inspection
                            : tagsState.meetingroom_patrol
                        }
                        onChange={handleCheckboxChange}
                      />
                    </td>
                  </tr>
                  {isInspection ? (
                    <>
                      <tr>
                        <td className="subTitleColumnAdditionalSetting">
                          <label htmlFor="wifiThreshold">Threshold for Wi-Fi Alerts</label>
                        </td>
                        <td valign="top">
                          <input
                            id="wifiThreshold"
                            className="inputField"
                            type="number"
                            style={{ width: "55px" }}
                            max="0"
                            value={wifiThreshold ?? ""}
                            onChange={(e) => {
                              setSaveButtonDisabled(false);
                              const value = e.target.value;
                              if (value === "") {
                                setWifiThreshold("");
                              } else {
                                const numValue = parseInt(value, 10);
                                if (numValue > 0) {
                                  setWifiThreshold(-numValue);
                                } else {
                                  setWifiThreshold(numValue);
                                }
                              }
                            }}
                          />
                          &ensp;dBm
                        </td>
                      </tr>
                      <tr>
                        <LocationSelection
                          onChange={(updatedLocations) =>
                            handleLocationChange("door", updatedLocations)
                          }
                          locationList={inspectionTagsData}
                          initialSelectedLocations={selectedSite?.door_detect_locations || []}
                          detectionType="door"
                        />
                      </tr>
                      <tr>
                        <LocationSelection
                          onChange={(updatedLocations) =>
                            handleLocationChange("display", updatedLocations)
                          }
                          locationList={inspectionTagsData}
                          initialSelectedLocations={
                            selectedSite?.display_detect_locations || []
                          }
                          detectionType="display"
                        />
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <td className="subTitleColumnAdditionalSetting">
                          <label htmlFor="inspectionTag">Include Inspection Locations</label>
                        </td>
                        <td valign="top">
                          <input
                            id="inspectionTag"
                            name="inspectionTag"
                            type="checkbox"
                            checked={tagsState.inspection_patrol}
                            onChange={handleCheckboxChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="subTitleColumnAdditionalSetting">
                          <label htmlFor="allowPatrolTpApp">
                            Allow Teleport Apps to Start Patrol
                          </label>
                        </td>
                        <td valign="top">
                          <input
                            id="allowPatrolTpApp"
                            name="allowPatrolTpApp"
                            type="checkbox"
                            checked={allowPatrolTpApp}
                            onChange={handleCheckboxChange}
                          />
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td className="individualContainerTitleColumn" valign="top"></td>
            <td style={{ padding: "0 0 15px 0" }}>
              <button
                onClick={handleSaveBehavior}
                className="saveButton"
                disabled={saveButtonDisabled}
              >
                Save
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
export default AdditionalRoundSettings;
